import * as Api from '@ViewModels';
import React from 'react';

export type UseSelectionStateType = {
	selectedRows: string[];
	excludedRows: string[];
	isAllSelected: boolean;
	selectionState: Api.ESelectionState;
	isPartialSelected: boolean;
	isSelectionStateAll: boolean;
	toggleRow: (rowId: string, isBulk?: boolean) => void;
	setRowsSelected: (rowIds: string[], selected: boolean) => void;
	toggleSelectAll: () => void;
	reset: () => void;
	isSelected: (rowId: string) => boolean;
};

type Props = {
	rows: string[] | null;
};

/**
 * useSelectionState Hook
 *
 * This hook provides a way to manage the selection state of rows in a table or list.
 * It handles the selection and exclusion of rows, as well as the overall selection state.
 *
 * Default behavior:
 * - Allows for single row selection
 * - Supports multi-row selection with "Select All" functionality
 *
 * Example usage:
 * ```
 * const MyComponent = ({ rows: ['item1', 'item2']}) => {
 *   const {
 *     selectedRows, // Array of selected row IDs, the id can be any unique string identifier on your list
 *     excludedRows, // Array of excluded row IDs, this is used when "Select All" is active
 *     isAllSelected, // Boolean indicating if all rows are selected
 *     selectionState, // Current selection state (None, Some, All)
 *     isPartialSelected, // Boolean indicating if some rows are selected
 *     isSelectionStateAll, // Boolean indicating if the selection state is "All"
 *     toggleRow, // Function to toggle the selection of a specific row
 *     toggleSelectAll, // Function to toggle the selection of all rows
 *     reset, // Function to reset the selection state
 *     isSelected // Function to check if a specific row is selected
 *   } = useSelectionState();
 *   return (
 *     <div>
 *       <button onClick={toggleSelectAll}>Toggle Select All</button>
 *       <button onClick={() => toggleRow('row1')}>Toggle Row 1</button>
 *       <button onClick={() => toggleRow('row2')}>Toggle Row 2</button>
 *       <button onClick={reset}>Reset</button>
 *       <div>Selected Rows: {JSON.stringify(selectedRows)}</div>
 *       <div>Excluded Rows: {JSON.stringify(excludedRows)}</div>
 *       <div>Is All Selected: {String(isAllSelected)}</div>
 *       <div>Selection State: {selectionState}</div>
 *       <div>Is Partial Selected: {String(isPartialSelected)}</div>
 *       <div>Is Selection State All: {String(isSelectionStateAll)}</div>
 *     </div>
 *   );
 * };
 * ```
 */
export const useSelectionState = ({ rows }: Props): UseSelectionStateType => {
	const [isAllSelected, setIsAllSelected] = React.useState(false);
	const [selectionState, setSelectionState] = React.useState<Api.ESelectionState>(Api.ESelectionState.None);
	const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
	const [excludedRows, setExcludedRows] = React.useState<string[]>([]);
	const [lastSelectedRow, setLastSelectedRow] = React.useState<string | null>(null);

	const toggleExcluded = (rowId: string, isBulk?: boolean) => {
		const currentExclusionIndex = excludedRows.indexOf(rowId);

		const nextExcludedRows = [...excludedRows];
		if (currentExclusionIndex === -1) {
			nextExcludedRows.push(rowId);
			setLastSelectedRow(rowId);

			setSelectionState(Api.ESelectionState.Some);
		} else {
			nextExcludedRows.splice(currentExclusionIndex, 1);
			setLastSelectedRow(null);

			if (nextExcludedRows.length === 0) {
				setSelectionState(Api.ESelectionState.All);
			}
		}

		if (isBulk && lastSelectedRow && lastSelectedRow !== rowId) {
			toggleRange(lastSelectedRow, rowId);
			setLastSelectedRow(rowId);
			return;
		}

		setExcludedRows(nextExcludedRows);
	};

	const isSelectionStateAll = React.useMemo(() => {
		return selectionState === Api.ESelectionState.All;
	}, [selectionState]);

	const toggleRange = React.useCallback(
		(start: string, end: string) => {
			if (!rows) {
				return;
			}

			const startIndex = rows.findIndex(c => c === start);
			const endIndex = rows.findIndex(c => c === end);

			if (startIndex === -1 || endIndex === -1) {
				return;
			}

			const minIndex = Math.min(startIndex, endIndex);
			const maxIndex = Math.max(startIndex, endIndex);

			for (let i = minIndex; i <= maxIndex; i++) {
				const rowsToSelect = rows[i];
				if (isAllSelected) {
					if (isSelected(rowsToSelect)) {
						excludedRows.push(rowsToSelect);
					}
				} else {
					if (!isSelected(rowsToSelect)) {
						selectedRows.push(rowsToSelect);
					}
				}
			}

			if (selectedRows.length === 0) {
				setSelectionState(Api.ESelectionState.None);
			} else {
				setSelectionState(Api.ESelectionState.Some);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[excludedRows, rows, selectedRows, selectionState]
	);

	/**
	 * Toggles the selection of a row.
	 *
	 * This function updates the selected rows and the selection state based on the provided row ID.
	 *
	 * if the isBulk parameter is provided, it will select a range of rows between the last selected row and the current row.
	 *
	 * @param rowId The ID of the row to toggle.
	 * @param isBulk Indicates if it is a bulk selection.
	 * @returns void
	 */
	const toggleRow = (rowId: string, isBulk?: boolean) => {
		if (isAllSelected) {
			toggleExcluded(rowId, isBulk);
			return;
		}

		const currentIndex = selectedRows.indexOf(rowId);
		const nextSelectedRows = [...selectedRows];

		if (currentIndex === -1) {
			setLastSelectedRow(rowId);
			nextSelectedRows.push(rowId);
		} else {
			nextSelectedRows.splice(currentIndex, 1);
			setLastSelectedRow(null);
		}

		if (isBulk && lastSelectedRow && lastSelectedRow !== rowId) {
			toggleRange(lastSelectedRow, rowId);
			setLastSelectedRow(rowId);
			return;
		}

		if (nextSelectedRows.length === 0) {
			setSelectionState(Api.ESelectionState.None);
		} else {
			setSelectionState(Api.ESelectionState.Some);
		}

		setSelectedRows(nextSelectedRows);
	};

	const setRowsSelected = (rowIds: string[], selected: boolean) => {
		if (isAllSelected) {
			const nextExcludedRows = selected
				? excludedRows.filter(id => !rowIds.includes(id))
				: [...excludedRows, ...rowIds.filter(id => !excludedRows.includes(id))];
			setExcludedRows(nextExcludedRows);
			setSelectionState(nextExcludedRows.length === 0 ? Api.ESelectionState.All : Api.ESelectionState.Some);
			return;
		}
		const nextSelectedRows = selected
			? [...selectedRows, ...rowIds.filter(id => !selectedRows.includes(id))]
			: selectedRows.filter(id => !rowIds.includes(id));
		setSelectedRows(nextSelectedRows);
		setSelectionState(nextSelectedRows.length === 0 ? Api.ESelectionState.None : Api.ESelectionState.Some);
	};

	const toggleSelectAll = React.useCallback(() => {
		if (selectedRows.length > 0 || selectionState === Api.ESelectionState.All) {
			setSelectionState(Api.ESelectionState.None);
			setIsAllSelected(false);
		} else {
			setSelectionState(Api.ESelectionState.All);
			setIsAllSelected(true);
		}

		setSelectedRows([]);
		setExcludedRows([]);
		setLastSelectedRow(null);
	}, [selectedRows.length, selectionState]);

	const reset = () => {
		setIsAllSelected(false);
		setSelectionState(Api.ESelectionState.None);
		setSelectedRows([]);
		setExcludedRows([]);
		setLastSelectedRow(null);
	};

	const isPartialSelected = React.useMemo(() => {
		return selectionState === Api.ESelectionState.Some;
	}, [selectionState]);

	const isSelected = React.useCallback(
		(rowId: string) => {
			if (isAllSelected) {
				return excludedRows.lastIndexOf(rowId) === -1;
			}
			return selectedRows.indexOf(rowId) !== -1;
		},
		[excludedRows, isAllSelected, selectedRows]
	);

	return {
		selectedRows,
		excludedRows,
		isAllSelected,
		selectionState,
		isPartialSelected,
		isSelectionStateAll,
		toggleRow,
		setRowsSelected,
		toggleSelectAll,
		reset,
		isSelected,
	};
};
